/*
  Neroli — card-hover.js

  Product cards ship their second image without a src, because loading="lazy"
  does not defer an image inside an already-visible card: every hover image
  downloaded on page load for a hover that might never happen, and that cannot
  happen at all on a touch device.

  This fills in the real src the first time a card is pointed at or focused.

  Delegated from document rather than bound per card, so cards appended by Load
  More are covered with no rebinding.

  pointerenter is used rather than pointerover because it does not fire when
  moving between children of the same card — but it also does not bubble, hence
  the capture phase. focusin does bubble, and is what covers keyboard users, who
  never fire a pointer event at all.
*/
(() => {
  const SELECTOR = '[data-hover-src]';

  function hydrate(image) {
    if (!image || image.dataset.hoverLoaded) return;
    /* Marked before assigning, so a pointer and a focus arriving together
       cannot both start the same download. */
    image.dataset.hoverLoaded = 'true';

    const { hoverSrcset, hoverSizes, hoverSrc } = image.dataset;
    if (hoverSrcset) image.setAttribute('srcset', hoverSrcset);
    if (hoverSizes) image.setAttribute('sizes', hoverSizes);
    if (hoverSrc) image.setAttribute('src', hoverSrc);
  }

  function hydrateWithin(node) {
    if (!node || node.nodeType !== 1) return;
    const image = node.querySelector?.(SELECTOR);
    if (image) hydrate(image);
  }

  function onPointer(event) {
    const card = event.target.closest?.('.product-card');
    if (card) hydrateWithin(card);
  }

  /* Capture, because pointerenter does not bubble. */
  document.addEventListener('pointerenter', onPointer, true);
  document.addEventListener('focusin', onPointer);

  /* A pointer already resting on a card when the script loads fires no further
     enter event, so that card would never hydrate. */
  document.addEventListener('pointermove', onPointer, { once: true, capture: true });
})();
